import Phaser from 'phaser';
import { TILE, PlayerId, PlayerStats, DEFAULT_STATS, PLAYER_BASE_SPEED, SPEED_PER_LEVEL } from '../types';
import { GridQuery } from './enemies/BaseEnemy';
import { scaleSpriteToTile, fitBodyToTile } from '../systems/SpriteScaling';

export interface PlayerKeys {
  up: Phaser.Input.Keyboard.Key;
  down: Phaser.Input.Keyboard.Key;
  left: Phaser.Input.Keyboard.Key;
  right: Phaser.Input.Keyboard.Key;
  plant: Phaser.Input.Keyboard.Key;
  remote: Phaser.Input.Keyboard.Key;
}

export const STARTING_LIVES = 3;
export const RESPAWN_INVULN_MS = 1500;

type Facing = 'down' | 'up' | 'left' | 'right';

export class Player extends Phaser.Physics.Arcade.Sprite {
  readonly id: PlayerId;
  readonly spawnCol: number;
  readonly spawnRow: number;
  displayName: string;
  stats: PlayerStats = { ...DEFAULT_STATS };
  alive = true;
  lives = STARTING_LIVES;
  invulnUntil = 0;
  private keys: PlayerKeys;
  private plantCooldown = 0;
  private onPlant: () => void;
  private onRemote: () => void;
  private onRespawnRequest: () => void;
  private grid: GridQuery | null = null;
  private facing: Facing = 'down';
  private walkBob = 0;
  private texPrefix: string; // 'player-geo' or 'player-aris'
  private glowRing!: Phaser.GameObjects.Graphics;

  constructor(
    scene: Phaser.Scene,
    id: PlayerId,
    displayName: string,
    col: number,
    row: number,
    keys: PlayerKeys,
    onPlant: () => void,
    onRemote: () => void,
    onRespawnRequest: () => void,
  ) {
    const x = col * TILE + TILE / 2;
    const y = row * TILE + TILE / 2;
    // P1 (Geo/George) = green character (player-aris), P2 (Aris) = red Mario (player-geo)
    const prefix = id === 'p1' ? 'player-aris' : 'player-geo';
    super(scene, x, y, `${prefix}-down`);
    this.id = id;
    this.spawnCol = col;
    this.spawnRow = row;
    this.displayName = displayName;
    this.keys = keys;
    this.onPlant = onPlant;
    this.onRemote = onRemote;
    this.onRespawnRequest = onRespawnRequest;
    this.texPrefix = prefix;

    scene.add.existing(this);
    this.baseScale = scaleSpriteToTile(this, TILE - 6);
    scene.physics.add.existing(this);
    fitBodyToTile(this, TILE - 12);
    const body = this.body as Phaser.Physics.Arcade.Body;
    body.setCollideWorldBounds(true);

    // Floor marker — a flat colored ellipse "under" the player + a small
    // arrow head above. Distinguishes players from each other and from
    // power-up shields without looking like an active power-up itself.
    const markerColor = id === 'p1' ? 0x52ff52 : 0xff5555;
    this.glowRing = scene.add.graphics();
    this.glowRing.setDepth((this.depth || 0) - 1);
    // Floor shadow ellipse (below feet)
    this.glowRing.fillStyle(markerColor, 0.55);
    this.glowRing.fillEllipse(0, TILE / 2 - 4, TILE - 10, 8);
    this.glowRing.lineStyle(1, markerColor, 0.9);
    this.glowRing.strokeEllipse(0, TILE / 2 - 4, TILE - 10, 8);
    // Small arrow head above the player pointing down
    this.glowRing.fillStyle(markerColor, 1);
    this.glowRing.fillTriangle(
      0, -TILE / 2 - 2,
      -6, -TILE / 2 - 12,
      6, -TILE / 2 - 12,
    );

    // Gentle pulse on the marker so the eye finds it
    scene.tweens.add({
      targets: this.glowRing,
      alpha: { from: 0.7, to: 1 },
      yoyo: true,
      repeat: -1,
      duration: 700,
      ease: 'Sine.easeInOut',
    });
  }

  update(time: number, _delta: number) {
    if (!this.alive) {
      this.setVelocity(0, 0);
      // Vieți nelimitate — orice apăsare pe tasta de bomb-plant te readuce
      // în viață, oricând. Counter-ul `lives` rămâne ca scor pentru cât de
      // bine te-ai descurcat, dar nu mai blochează respawn-ul.
      if (Phaser.Input.Keyboard.JustDown(this.keys.plant)) {
        this.onRespawnRequest();
      }
      return;
    }
    const body = this.body as Phaser.Physics.Arcade.Body;
    const speed = PLAYER_BASE_SPEED + this.stats.speedLevel * SPEED_PER_LEVEL;

    let vx = 0;
    let vy = 0;
    if (this.keys.left.isDown) vx -= speed;
    if (this.keys.right.isDown) vx += speed;
    if (this.keys.up.isDown) vy -= speed;
    if (this.keys.down.isDown) vy += speed;

    // Lock to one axis to ease grid navigation
    if (vx !== 0 && vy !== 0) {
      if (Math.abs(body.velocity.x) >= Math.abs(body.velocity.y)) vy = 0;
      else vx = 0;
    }

    [vx, vy] = this.applyLaneAndCornerSlide(vx, vy, speed);
    body.setVelocity(vx, vy);

    this.updateFacingAndAnimation(vx, vy, _delta);
    if (this.glowRing) this.glowRing.setPosition(this.x, this.y);

    if (Phaser.Input.Keyboard.JustDown(this.keys.plant) && time > this.plantCooldown) {
      this.plantCooldown = time + 150;
      this.onPlant();
    }
    if (this.stats.hasRemote && Phaser.Input.Keyboard.JustDown(this.keys.remote)) {
      this.onRemote();
    }
  }

  takeDamage(): boolean {
    if (!this.alive) return false;
    if (this.scene.time.now < this.invulnUntil) return false;
    if (this.stats.hasShield) {
      this.stats.hasShield = false;
      this.invulnUntil = this.scene.time.now + 800;
      this.scene.tweens.add({ targets: this, alpha: 0.3, yoyo: true, duration: 100, repeat: 4 });
      return false;
    }
    this.alive = false;
    // Vieți nelimitate — nu mai scădem `lives`. Counter-ul rămâne afișat ca ∞
    // în HUD; respawn este garantat oricând prin tasta plant.
    this.setTint(0x555555);
    this.setVelocity(0, 0);
    this.glowRing?.setVisible(false);
    return true;
  }

  respawn(col?: number, row?: number) {
    this.alive = true;
    this.clearTint();
    this.setAlpha(1);
    this.glowRing?.setVisible(true);
    const c = col ?? this.spawnCol;
    const r = row ?? this.spawnRow;
    this.setPosition(c * TILE + TILE / 2, r * TILE + TILE / 2);
    this.invulnUntil = this.scene.time.now + RESPAWN_INVULN_MS;
    // Blink during invulnerability
    this.scene.tweens.add({
      targets: this,
      alpha: { from: 1, to: 0.3 },
      yoyo: true,
      repeat: Math.floor(RESPAWN_INVULN_MS / 200),
      duration: 100,
      onComplete: () => this.setAlpha(1),
    });
  }

  gridCol(): number {
    return Math.floor(this.x / TILE);
  }
  gridRow(): number {
    return Math.floor(this.y / TILE);
  }

  attachGrid(grid: GridQuery) {
    this.grid = grid;
  }

  private baseScale = 1;

  private setFacing(f: Facing) {
    if (this.facing === f) return;
    this.facing = f;
    // Per-character side sprite orientation: Geo's side art naturally faces
    // LEFT, Aris's side art naturally faces RIGHT. Flip accordingly so the
    // hero always faces the direction they're walking.
    const sideFacesLeftByDefault = this.id === 'p1';
    switch (f) {
      case 'down':  this.setTexture(`${this.texPrefix}-down`);  this.setFlipX(false); break;
      case 'up':    this.setTexture(`${this.texPrefix}-up`);    this.setFlipX(false); break;
      case 'left':  this.setTexture(`${this.texPrefix}-side`);  this.setFlipX(!sideFacesLeftByDefault); break;
      case 'right': this.setTexture(`${this.texPrefix}-side`);  this.setFlipX(sideFacesLeftByDefault);  break;
    }
    // Re-scale and re-fit body — different facings can have different native sizes
    this.baseScale = scaleSpriteToTile(this, TILE - 6);
    fitBodyToTile(this, TILE - 12);
  }

  private updateFacingAndAnimation(vx: number, vy: number, delta: number) {
    const moving = vx !== 0 || vy !== 0;
    if (moving) {
      if (Math.abs(vx) > Math.abs(vy)) this.setFacing(vx > 0 ? 'right' : 'left');
      else                              this.setFacing(vy > 0 ? 'down'  : 'up');
      // Tiny scale pulse for "step" feel — keeps body collision stable.
      this.walkBob += delta * 0.012;
      const pulse = 1 + Math.sin(this.walkBob) * 0.025;
      this.setScale(this.baseScale * pulse);
    } else {
      this.walkBob = 0;
      this.setScale(this.baseScale);
    }
  }

  /**
   * Two behaviours bundled:
   * 1. Lane snap — when moving along an axis, gently center the player on the
   *    perpendicular axis so they don't drift and get stuck on tile edges.
   * 2. Corner slide — when the forward tile is blocked but a lateral tile
   *    in the same direction is open, nudge sideways toward the open lane.
   */
  private applyLaneAndCornerSlide(vx: number, vy: number, speed: number): [number, number] {
    if (!this.grid) return [vx, vy];
    const col = Math.floor(this.x / TILE);
    const row = Math.floor(this.y / TILE);
    const colCenterX = col * TILE + TILE / 2;
    const rowCenterY = row * TILE + TILE / 2;
    const SNAP_TOL = 14;          // start gentle attraction this close to center
    const CORNER_REACH = TILE * 0.85; // how far off-center we still attempt slide

    // ---- Vertical motion ----
    if (vy !== 0 && vx === 0) {
      const targetRow = row + Math.sign(vy);
      const forwardBlocked = this.grid.isBlocked(col, targetRow);

      if (forwardBlocked) {
        // Corner slide: check both lateral neighbors
        for (const dc of [-1, 1] as const) {
          const sideCol = col + dc;
          if (this.grid.isBlocked(sideCol, row)) continue;
          if (this.grid.isBlocked(sideCol, targetRow)) continue;
          const offsetFromCol = this.x - colCenterX;
          // Slide toward the side we're already leaning into, OR if very close
          // to the column center we still help by picking the open side.
          const leaningRight = offsetFromCol > 1;
          const leaningLeft  = offsetFromCol < -1;
          const matchSide = (dc === 1 && leaningRight) || (dc === -1 && leaningLeft);
          const nearlyCentered = Math.abs(offsetFromCol) <= 1;
          if ((matchSide || nearlyCentered) && Math.abs(offsetFromCol) < CORNER_REACH) {
            return [dc * speed, 0];
          }
        }
      } else {
        // Forward open — gently snap onto column center so we don't drift off-lane
        const drift = colCenterX - this.x;
        if (Math.abs(drift) > 0 && Math.abs(drift) <= SNAP_TOL) {
          return [Math.sign(drift) * Math.min(speed, Math.abs(drift) * 12), vy];
        }
      }
    }

    // ---- Horizontal motion ----
    if (vx !== 0 && vy === 0) {
      const targetCol = col + Math.sign(vx);
      const forwardBlocked = this.grid.isBlocked(targetCol, row);

      if (forwardBlocked) {
        for (const dr of [-1, 1] as const) {
          const sideRow = row + dr;
          if (this.grid.isBlocked(col, sideRow)) continue;
          if (this.grid.isBlocked(targetCol, sideRow)) continue;
          const offsetFromRow = this.y - rowCenterY;
          const leaningDown = offsetFromRow > 1;
          const leaningUp   = offsetFromRow < -1;
          const matchSide = (dr === 1 && leaningDown) || (dr === -1 && leaningUp);
          const nearlyCentered = Math.abs(offsetFromRow) <= 1;
          if ((matchSide || nearlyCentered) && Math.abs(offsetFromRow) < CORNER_REACH) {
            return [0, dr * speed];
          }
        }
      } else {
        const drift = rowCenterY - this.y;
        if (Math.abs(drift) > 0 && Math.abs(drift) <= SNAP_TOL) {
          return [vx, Math.sign(drift) * Math.min(speed, Math.abs(drift) * 12)];
        }
      }
    }

    return [vx, vy];
  }
}
